import React, { FC, useCallback, useMemo } from 'react';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import LockIcon from '@mui/icons-material/Lock';
import PeopleIcon from '@mui/icons-material/People';
import { Box, Grid, Tooltip, Typography, useTheme } from '@mui/material';
import { TournamentAccessibility } from '@okletsplay/core';
import { Route } from 'app/enums';
import { TournamentEntity } from 'app/interfaces/entity';
import classNames from 'classnames';
import { TournamentStatus } from 'modules/tournaments/components/TournamentStatus';
import { useTournamentPlatformType } from 'modules/tournaments/hooks';
import { useTournamentCurrentPrizes } from 'modules/tournaments/hooks/use-tournament-current-prizes';
import { useNavigate } from 'react-router-dom';
import { TournamentFeaturedButton } from 'shared/components/FeaturedButton';
import { GameCurrency, GameCurrencyWithFee } from 'modules/game-payments/components';
import { GameActivityType } from 'shared/enums';
import { TournamentSponsoredIndicator } from 'shared/components/TournamentSponsoredIndicator';
import { useStyles } from './TournamentTile.styles';
import { getAssetTransformerUrl } from 'shared/utils';
import { Picture, ResponsiveImageSrc } from 'shared/components/Picture';
import { GameActivityPaymentProvider } from 'modules/game-payments/providers';
import { TournamentTileFooterTooltipContent } from './TournamentTileFooterTooltipContent';
import { TournamentTileTypeLabel } from './TournamentTileTypeLabel';

interface TournamentTileProps {
  tournament: TournamentEntity;
  onClick: (identifier: string) => void;
}

const TournamentTileComponent: FC<TournamentTileProps> = ({ tournament, onClick }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const height = parseFloat(theme.spacing(14));

  const [defaultSrc, ...sources] = useMemo((): ResponsiveImageSrc[] => {
    if (!tournament || !tournament.game?.activityImage) return [];

    const gameBannerKey = tournament.game.activityImage.split('.com/')[1];
    const key = tournament.cardImageKey || gameBannerKey;

    const getSrc = (density = 1): string => {
      return getAssetTransformerUrl(key, {
        resize: { height: height * density },
      });
    };

    return [
      {
        getSrc,
      },
      {
        densities: [1, 1.5, 2],
        getSrc,
      },
    ];
  }, [tournament, height]);

  const { classes } = useStyles();
  const { checkIsKnockoutEnabled } = useTournamentPlatformType();
  const isKnockoutEnabled = checkIsKnockoutEnabled(tournament?.game?.platformType);

  const { currentDistributionBreakdown, currentTotalPrize: totalPrize } =
    useTournamentCurrentPrizes(tournament);
  const firstPlacePrize = currentDistributionBreakdown?.[0]?.amount;
  const { playerEntryFee: entryFee } = tournament.prizeAndDistribution;

  const teamsSize = useMemo(() => {
    if (tournament.teamsSettings.minTeams === tournament.teamsSettings.maxTeams) {
      return tournament.teamsSettings.minTeams;
    } else {
      return `${tournament.teamsSettings.minTeams}-${tournament.teamsSettings.maxTeams}`;
    }
  }, [tournament.teamsSettings.maxTeams, tournament.teamsSettings.minTeams]);

  const isSinglePlayer = useMemo(() => {
    return tournament?.teamsSettings?.teamSize === 1;
  }, [tournament]);

  const isPrivateTournament = useMemo(() => {
    return tournament?.accessibility === TournamentAccessibility.Private;
  }, [tournament.accessibility]);

  const footerTooltip = (
    <TournamentTileFooterTooltipContent
      entryFee={entryFee}
      firstPlacePrize={firstPlacePrize}
      totalPrize={totalPrize}
      teamsSize={teamsSize}
      isSinglePlayer={isSinglePlayer}
    />
  );
  const onClickHandler = useCallback(() => {
    if (isKnockoutEnabled) {
      navigate(Route.TournamentOverview.replace(':id', tournament?.id));
      return;
    }

    onClick?.(tournament?.id);
  }, [isKnockoutEnabled, navigate, onClick, tournament?.id]);

  return (
    <Box className={classNames(classes.root, classes[tournament.status])} onClick={onClickHandler}>
      <Box className={classes.header}>
        <Picture defaultSrc={defaultSrc} sources={sources} />
        {totalPrize || tournament.isSponsored ? (
          <Box className={classes.badgeContainer}>
            {tournament.isSponsored && (
              <Box className={classes.sponsoredBadge}>
                <TournamentSponsoredIndicator />
              </Box>
            )}
            {totalPrize ? (
              <Box className={classes.badge}>
                <EmojiEventsIcon /> <GameCurrency amount={totalPrize} />
              </Box>
            ) : null}
          </Box>
        ) : null}
        <Box className={classes.indicatorsContainer}>
          <TournamentFeaturedButton value={tournament.isFeatured} entity={tournament} />
        </Box>
      </Box>

      <Box className={classes.body}>
        <TournamentTileTypeLabel tournament={tournament} />
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Tooltip title={tournament.name}>
            <Typography className={classes.title}>{tournament.name}</Typography>
          </Tooltip>
          {isPrivateTournament && <LockIcon className={classes.lockIcon} />}
        </Box>
        <TournamentStatus tournament={tournament} />
      </Box>

      <Tooltip title={footerTooltip} enterDelay={500}>
        <Grid container className={classes.footer} columnSpacing={1} columns={7}>
          <Grid item xs={2} className={classes.footerItem}>
            <Box className={classes.footerItemContent}>
              <PeopleIcon className={classes.smallIcon} fontSize="small" />
              <Typography component="span" fontSize="inherit">
                {teamsSize}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={5} className={classes.footerItem}>
            <Box className={classes.footerItemContent}>
              <LocalActivityIcon className={classes.smallIcon} fontSize="small" />
              <GameCurrencyWithFee amount={entryFee} showZeroLabel />
            </Box>
          </Grid>
        </Grid>
      </Tooltip>
    </Box>
  );
};

export const TournamentTile: FC<TournamentTileProps> = (props) => {
  return (
    <GameActivityPaymentProvider
      gameActivity={props.tournament}
      activityType={GameActivityType.Tournament}
    >
      <TournamentTileComponent {...props} />
    </GameActivityPaymentProvider>
  );
};
